import { useState, useEffect } from "react";
import { runQuery, getAylikRapor, getSozlesmeler, getStokGCOzet, getCubiclGroups, getCubiclTasks } from "../services/api";

const CUBICL_PROJE_ADI = "EMK Yazılım";

async function fetchCubiclBekleyen() {
  const groupsRes = await getCubiclGroups();
  const emk = (groupsRes.data || []).find((g) => g.name === CUBICL_PROJE_ADI);
  if (!emk) return [];
  const tasksRes = await getCubiclTasks(emk._id, { archived: "false", limit: 300 });
  return (tasksRes.data || [])
    .filter((t) => t.state === "cb_waiting")
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

const AYLAR = [
  "", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

function donem(yil, ay) {
  return `${yil}-${String(ay).padStart(2, "0")}`;
}

const NOW = new Date();
const CUR_YIL = NOW.getFullYear();
const CUR_AY = NOW.getMonth() + 1;
const CUR_DONEM = donem(CUR_YIL, CUR_AY);

let NEXT_YIL = CUR_YIL, NEXT_AY = CUR_AY + 1;
if (NEXT_AY > 12) { NEXT_AY = 1; NEXT_YIL += 1; }
const NEXT_DONEM = donem(NEXT_YIL, NEXT_AY);

function fmt(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
}

function computeNakitKPI(rows) {
  let buAy = 0, geciken = 0, gelecek = 0;
  (rows || []).forEach(({ YIL, AY, TUTAR }) => {
    const t = Number(TUTAR) || 0;
    const d = donem(YIL, AY);
    if (d === CUR_DONEM) buAy += t;
    else if (d < CUR_DONEM) geciken += t;
    else gelecek += t;
  });
  return { buAy, geciken, gelecek, toplam: buAy + geciken + gelecek };
}

const ALICI_BORC_SQL = `
SELECT YIL, AY, SUM(TUTAR) AS TUTAR
FROM EMW_NAKITAKIS
WHERE TIP = '102-Alici Borc'
GROUP BY YIL, AY
`.trim();

const SATICI_ALACAK_SQL = `
SELECT YIL, AY, ABS(SUM(TUTAR)) AS TUTAR
FROM EMW_NAKITAKIS
WHERE TIP = '103-Satici Alacak'
GROUP BY YIL, AY
`.trim();

function MiniKpi({ label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 110 }}>
      <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Panel({ icon, title, onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--bg-card-alt)", border: "1px solid var(--accent-bg)", borderRadius: 12,
        padding: 20, cursor: onClick ? "pointer" : "default",
        height: "100%", boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-text)" }}>{icon} {title}</div>
        {onClick && <span style={{ fontSize: 12, color: "var(--text-faint)" }}>Detaya git →</span>}
      </div>
      {children}
    </div>
  );
}

export default function Anasayfa({ onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [alici, setAlici] = useState(null);
  const [satici, setSatici] = useState(null);
  const [sozlesmeRapor, setSozlesmeRapor] = useState([]);
  const [sozlesmeler, setSozlesmeler] = useState([]);
  const [gelirGider, setGelirGider] = useState(null);
  const [cubiclBekleyen, setCubiclBekleyen] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      runQuery(ALICI_BORC_SQL),
      runQuery(SATICI_ALACAK_SQL),
      getAylikRapor(CUR_YIL, CUR_AY),
      getSozlesmeler(),
      getStokGCOzet({ yil: CUR_YIL, ay: CUR_AY }),
      fetchCubiclBekleyen(),
    ]).then(([r1, r2, r3, r4, r5, r6]) => {
      if (r1.status === "fulfilled") setAlici(computeNakitKPI(r1.value.data.rows));
      if (r2.status === "fulfilled") setSatici(computeNakitKPI(r2.value.data.rows));
      if (r3.status === "fulfilled") setSozlesmeRapor(r3.value.data || []);
      if (r4.status === "fulfilled") setSozlesmeler(r4.value.data || []);
      if (r5.status === "fulfilled") setGelirGider((r5.value.data.rows || [])[0] || null);
      if (r6.status === "fulfilled") setCubiclBekleyen(r6.value || []);
    }).finally(() => setLoading(false));
  }, []);

  const sozToplam = sozlesmeRapor.reduce((s, r) => s + (r.Tutar || 0), 0);
  const sozFaturalanmis = sozlesmeRapor.filter((r) => r.Faturalanmis).reduce((s, r) => s + (r.Tutar || 0), 0);
  const sozBekleyen = sozToplam - sozFaturalanmis;

  const bitenSozlesmeler = sozlesmeler.filter((s) => {
    if (!s.BitisTarihi) return false;
    const d = s.BitisTarihi.slice(0, 7);
    return d === CUR_DONEM || d === NEXT_DONEM;
  });

  if (loading) {
    return <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "60px 0", textAlign: "center" }}>⏳ Yükleniyor...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontSize: 13, color: "var(--text-faint)" }}>
        {AYLAR[CUR_AY]} {CUR_YIL} özet görünümü
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        <Panel icon="💳" title="Alıcı Borç" onClick={() => onNavigate?.("alici-borc")}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <MiniKpi label="Geçiken" value={fmt(alici?.geciken)} color="#fca5a5" />
            <MiniKpi label="Bu Ay" value={fmt(alici?.buAy)} color="#93c5fd" />
            <MiniKpi label="Gelecek" value={fmt(alici?.gelecek)} color="#86efac" />
            <MiniKpi label="Toplam" value={fmt(alici?.toplam)} color="var(--text-primary)" />
          </div>
        </Panel>

        <Panel icon="🏭" title="Satıcı Alacak" onClick={() => onNavigate?.("satici-alacak")}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <MiniKpi label="Geçiken" value={fmt(satici?.geciken)} color="#fca5a5" />
            <MiniKpi label="Bu Ay" value={fmt(satici?.buAy)} color="#93c5fd" />
            <MiniKpi label="Gelecek" value={fmt(satici?.gelecek)} color="#86efac" />
            <MiniKpi label="Toplam" value={fmt(satici?.toplam)} color="var(--text-primary)" />
          </div>
        </Panel>

        <div style={{ gridRow: "span 2" }}>
          <Panel icon="🗂️" title={`Cubicl — Bekleyen İşler (${cubiclBekleyen.length})`} onClick={() => onNavigate?.("kayitlar")}>
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {cubiclBekleyen.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-ghost)" }}>Bekleyen görev yok.</div>
              ) : (
                cubiclBekleyen.map((t) => (
                  <div key={t._id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-hairline)" }}>
                    <div style={{ fontSize: 12, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.name || "(konu yok)"}
                    </div>
                    {t.customer?.name && (
                      <div style={{ fontSize: 11, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.customer.name}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Panel>
        </div>

        <Panel icon="💰" title="Gelir / Gider" onClick={() => onNavigate?.("stok-gc")}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <MiniKpi label="Gelir" value={fmt(gelirGider?.TOPLAM_GELIR)} color="#34d399" />
            <MiniKpi label="Gider" value={fmt(gelirGider?.TOPLAM_GIDER)} color="#fb923c" />
            <MiniKpi label="Net" value={fmt(gelirGider?.NET)} color={(gelirGider?.NET || 0) >= 0 ? "#60a5fa" : "#fca5a5"} />
          </div>
        </Panel>

        <Panel icon="📋" title="Destek Sözleşmeleri" onClick={() => onNavigate?.("sozlesme")}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <MiniKpi label="Sözleşme Bedeli" value={fmt(sozToplam)} color="var(--text-primary)" />
            <MiniKpi label="Faturalanan" value={fmt(sozFaturalanmis)} color="#34d399" />
            <MiniKpi label="Bekleyen" value={fmt(sozBekleyen)} color="#fb923c" />
          </div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
            Bu Ay / Önümüzdeki Ay Biten Sözleşmeler
          </div>
          {bitenSozlesmeler.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-ghost)" }}>Bu ay ve önümüzdeki ay biten sözleşme yok.</div>
          ) : (
            bitenSozlesmeler.map((s) => (
              <div key={s.ID} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "5px 0", borderBottom: "1px solid var(--border-hairline)" }}>
                <span style={{ color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.CariIsmi}</span>
                <span style={{ color: s.BitisTarihi.slice(0, 7) === CUR_DONEM ? "#fca5a5" : "#fbbf24", fontWeight: 600, flexShrink: 0 }}>
                  {s.BitisTarihi.slice(0, 10)}
                </span>
              </div>
            ))
          )}
        </Panel>
      </div>
    </div>
  );
}
